import React, { MouseEvent, useState } from 'react';

import { Box, IconButton, Stack, Typography } from '@mui/material';

import { useAppDispatch, useAppSelector } from 'state/hooks';
import {
  deleteDraftEditableSpecialDate,
  deleteDraftNewSpecialDate,
  selectSpecialEventDates,
  updateDraftSpecialDate,
  useSpecialEventDateDeleteMutation,
} from 'state/userAdminSlices';
import { selectProject } from 'state/workflowSlice';

import { SpecialDateRequest } from 'appTypes';
import { ConfirmDeletePopup } from 'features/common/ConfirmDeletePopup';
import FontAwesomeIcon from 'features/common/FontAwesomeIcon';
import StlTable from 'features/common/StlTable/StlTable';
import { useSubscription } from 'features/common/hooks/useSubscription';
import { stl_secondary_hilited_background_color } from 'theme/cemTheme';

import { useUprStatus } from '../hooks';
import AddSpecialEventBtn from './AddSpecialEventBtn';
import SpecialDateSetting from './SpecialDateSettings';
import { columns, isProjectActive } from './utils';

import styles from './SpecialDateRequest.module.css';

export default function SpecialDateRequestTable() {
  const dispatch = useAppDispatch();
  const uprDates = useAppSelector(selectSpecialEventDates);
  const project_slug = useAppSelector(selectProject);
  const [deleting, setDeleting] = useState<SpecialDateRequest | null>(null);
  const [editing, setEditing] = useState<SpecialDateRequest | null>(null);

  const { subscription, project_data } = useSubscription();
  const { isNew: isUprNew, isEditable: isUprEditable } = useUprStatus();
  const [deleteSpecialDate] = useSpecialEventDateDeleteMutation();
  const specialDates = project_data?.special_date_requests ?? uprDates;
  const projectActive = isProjectActive(project_data);

  const handleDelete = () => {
    if (!deleting) return;
    const { hash_id, tempId } = deleting;
    if (isUprNew)
      dispatch(
        deleteDraftNewSpecialDate({
          subscription_id: subscription.subscription_id,
          tempId,
        }),
      );
    else if (isUprEditable)
      dispatch(
        deleteDraftEditableSpecialDate({
          subscription_id: subscription.subscription_id,
          hash_id,
        }),
      );
    else deleteSpecialDate({ project_slug, hash_id });

    setDeleting(null);
  };

  const beforeDeleteNew = (
    e: MouseEvent<HTMLButtonElement>,
    req: SpecialDateRequest,
  ) => {
    e.stopPropagation();
    setDeleting(req);
  };

  const startEdit = (e: MouseEvent, req: SpecialDateRequest) => {
    e.stopPropagation();
    setEditing(req);
  };

  const handleEditSave = (subscription_id: string, req: SpecialDateRequest) => {
    dispatch(
      updateDraftSpecialDate({
        subscription_id,
        date_request: req,
      }),
    );
  };

  const isDeletableByProjectRules = (r: SpecialDateRequest) =>
    projectActive && r.can_delete;

  const canDelete = (r: SpecialDateRequest) =>
    isUprEditable || isDeletableByProjectRules(r);

  const showBtn = specialDates?.length > 0;

  return (
    <>
      <Box className={styles.tableWrapper}>
        <StlTable
          data={specialDates}
          columns={columns}
          renderEmptyRowsFallback={({ table }) => (
            <Stack className={styles.emptyRowsWrapper}>
              <EmptyTableMsg isSubAdmin={subscription.user_is_sub_admin} />
              <AddSpecialEventBtn />
            </Stack>
          )}
          renderRowActions={({ row }) => {
            const req = row.original;
            return (
              <>
                {isUprEditable && (
                  <IconButton onClick={(e) => startEdit(e, req)}>
                    <FontAwesomeIcon name="pen" size="0.75rem" />
                  </IconButton>
                )}
                {canDelete(req) && (
                  <IconButton onClick={(e) => beforeDeleteNew(e, req)}>
                    <FontAwesomeIcon name="trash" size="0.75rem" />
                  </IconButton>
                )}
              </>
            );
          }}
        />
        {showBtn && (
          <Box height="40px" alignContent="center">
            <AddSpecialEventBtn />
          </Box>
        )}
      </Box>
      <ConfirmDeletePopup
        objectType="Special Event"
        isOpen={Boolean(deleting)}
        setIsOpen={() => setDeleting(null)}
        onApply={handleDelete}
        name={deleting?.name ?? ''}
      />
      <SpecialDateSetting
        onSave={handleEditSave}
        initialValue={editing}
        open={Boolean(editing)}
        onClose={() => setEditing(null)}
      />
    </>
  );
}

export function SpecialEventHeader({
  lightCol = false,
}: {
  lightCol?: boolean;
}) {
  return (
    <>
      <FontAwesomeIcon
        name="clock-rotate-left"
        type="duotone-regular"
        size="4rem"
        sx={{
          color: lightCol ? stl_secondary_hilited_background_color : '#313131',
        }}
      />
      <Typography variant="h4" p={0.5}>
        Special Events
      </Typography>
    </>
  );
}

function EmptyTableMsg({ isSubAdmin }: { isSubAdmin: boolean }) {
  return (
    <Box width="fit-content" margin="0 auto">
      <Stack gap={1} alignItems="center">
        <Stack alignItems="center">
          <SpecialEventHeader />
          <Typography variant="body2" fontSize="0.75rem" p={0.5}>
            {isSubAdmin
              ? `Add past or future dates to compare and discover new insights.`
              : `Talk to your subscription administrator to request special event dates.`}
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
}
